import {
  getTextContent,
  isAssistantMessage,
  isToolMessage,
  type ConversationMessage,
} from '../../types/conversation-message';
import {
  setAssistantMessageFeedback,
  type AssistantMessageFeedback,
} from '../../stores/message-feedback-store';
import {
  captureCueboardFeatureOutcomeEvent,
  captureCueboardPostHogEvent,
} from '@/utils/posthog';

const FEEDBACK_EVENT_NAME = 'chat_assistant_feedback_submitted';

type AssistantMessageFeedbackProperties = {
  feedback: AssistantMessageFeedback;
  user_message_id: string;
  assistant_message_count: number;
  assistant_message_ids: string[];
  assistant_content_types: string[];
  assistant_text_length: number;
  tool_message_count: number;
  has_error: boolean;
};

export function buildAssistantMessageFeedbackProperties({
  feedback,
  messages,
  userMessageId,
}: {
  feedback: AssistantMessageFeedback;
  messages: ConversationMessage[];
  userMessageId: string;
}): AssistantMessageFeedbackProperties {
  const assistantMessages = messages.filter(isAssistantMessage);
  const toolMessages = messages.filter(isToolMessage);
  const assistantMessageIds = assistantMessages
    .map(message => message.id)
    .filter(messageId => messageId.length > 0);
  const assistantContentTypes = Array.from(
    new Set(
      assistantMessages.flatMap(message =>
        (message.content ?? []).map(content => content.type)
      )
    )
  );
  const assistantTextLength = assistantMessages.reduce(
    (total, message) =>
      total +
      getTextContent(message).reduce((messageTotal, text) => {
        return messageTotal + text.length;
      }, 0),
    0
  );

  return {
    feedback,
    user_message_id: userMessageId,
    assistant_message_count: assistantMessages.length,
    assistant_message_ids: assistantMessageIds,
    assistant_content_types: assistantContentTypes,
    assistant_text_length: assistantTextLength,
    tool_message_count: toolMessages.length,
    has_error: messages.some(message => Boolean(message.error)),
  };
}

export async function submitAssistantMessageFeedback({
  feedback,
  messages,
  userMessageId,
}: {
  feedback: AssistantMessageFeedback;
  messages: ConversationMessage[];
  userMessageId?: string;
}) {
  if (!userMessageId) {
    return false;
  }

  captureCueboardPostHogEvent(
    FEEDBACK_EVENT_NAME,
    buildAssistantMessageFeedbackProperties({
      feedback,
      messages,
      userMessageId,
    })
  );

  setAssistantMessageFeedback(userMessageId, feedback);

  const messagesBridge = window.jsb?.MessagesBridge as
    | Partial<Window['jsb']['MessagesBridge']>
    | undefined;
  const bridgeMethod =
    feedback === 'good'
      ? messagesBridge?.goodMessage
      : messagesBridge?.badMessage;
  const bridgeMethodName =
    feedback === 'good'
      ? 'MessagesBridge.goodMessage'
      : 'MessagesBridge.badMessage';

  let bridgeFailed = false;
  const bridgeStartedAt = Date.now();
  if (bridgeMethod) {
    try {
      await bridgeMethod(userMessageId);
    } catch (error) {
      bridgeFailed = true;
      captureCueboardFeatureOutcomeEvent({
        featureKey: 'chat.assistant_feedback',
        surface: 'embedded_chat',
        outcome: 'failure',
        toolName: bridgeMethodName,
        durationMs: Date.now() - bridgeStartedAt,
        failureClass: 'client.network_error',
        error,
        properties: {
          feedback,
          user_message_id: userMessageId,
          suspect_layer: 'swift_bridge',
        },
      });
      console.error('[AssistantMessageOperations] feedback bridge failed', {
        feedback,
        userMessageId,
        error,
      });
    }
  }

  if (bridgeMethod && !bridgeFailed) {
    captureCueboardFeatureOutcomeEvent({
      featureKey: 'chat.assistant_feedback',
      surface: 'embedded_chat',
      outcome: 'success',
      toolName: bridgeMethodName,
      durationMs: Date.now() - bridgeStartedAt,
      properties: {
        feedback,
        user_message_id: userMessageId,
        swift_bridge_available: true,
      },
    });
  }

  return true;
}
